/* ===== Nucleo Sidebar Navigation ===== */

import { useEffect, useState } from 'react'
import { NavLink } from 'react-router-dom'
import {
  LayoutDashboard,
  Megaphone,
  Users,
  Calendar,
  Briefcase,
  MessageSquare,
  FileText,
  HeartHandshake,
  DollarSign,
  Handshake,
  ShoppingCart,
  Settings,
  ShieldCheck,
  ShieldAlert,
  Building2,
  X,
  Sparkles,
  ClipboardList,
  CreditCard,
} from 'lucide-react'
import { isModuleEnabled, type OrgModuleKey } from '@zetyar/domain'
import { useApp } from '@/presentation/providers/useApp'
import { createWebTenantContext } from '@/infrastructure/repositories/announcements'
import { webChatRepository } from '@/infrastructure/repositories/chat'

const UNREAD_POLL_INTERVAL_MS = 15_000

interface SidebarProps {
  isOpen?: boolean
  onClose?: () => void
}

export function Sidebar({ isOpen = false, onClose }: SidebarProps) {
  const { currentOrganization, currentMembership, profile, user, permissions } = useApp()
  const canViewAudit = profile?.isSuperAdmin || permissions.includes('audit.view')
  const canManageOrganization = permissions.includes('organization.manage')
  const [hasUnreadChat, setHasUnreadChat] = useState(false)

  useEffect(() => {
    if (!user || !profile || !currentOrganization || !webChatRepository.getUnreadChannelIds) {
      setHasUnreadChat(false)
      return
    }

    let isActive = true
    const context = createWebTenantContext({
      authUid: user.uid,
      personId: profile.id,
      organizationId: currentOrganization.id,
      membershipId: currentMembership?.id,
    })

    const poll = async () => {
      try {
        const unreadIds = await webChatRepository.getUnreadChannelIds!(context)
        if (isActive) setHasUnreadChat(unreadIds.length > 0)
      } catch (error) {
        console.error('Error al revisar mensajes no leidos:', error)
      }
    }

    void poll()
    const intervalId = setInterval(() => void poll(), UNREAD_POLL_INTERVAL_MS)

    const handleChatRead = () => {
      void poll()
    }
    window.addEventListener('chat-read', handleChatRead)

    return () => {
      isActive = false
      clearInterval(intervalId)
      window.removeEventListener('chat-read', handleChatRead)
    }
  }, [user, profile, currentOrganization, currentMembership])

  const allNavItems: {
    to: string
    label: string
    icon: typeof LayoutDashboard
    moduleKey?: OrgModuleKey
  }[] = [
    { to: '/dashboard', label: 'Inicio', icon: LayoutDashboard },
    { to: '/announcements', label: 'Comunicados', icon: Megaphone, moduleKey: 'announcements' },
    { to: '/members', label: 'Miembros', icon: Users, moduleKey: 'members' },
    { to: '/events', label: 'Actividades', icon: Calendar, moduleKey: 'events' },
    { to: '/projects', label: 'Proyectos', icon: Briefcase, moduleKey: 'projects' },
    { to: '/chat', label: 'Chat', icon: MessageSquare, moduleKey: 'chat' },
    { to: '/documents', label: 'Documentos', icon: FileText, moduleKey: 'documents' },
    { to: '/campaigns', label: 'Campañas', icon: HeartHandshake, moduleKey: 'campaigns' },
    { to: '/finance', label: 'Finanzas', icon: DollarSign, moduleKey: 'finance' },
    { to: '/dues', label: 'Cuotas', icon: CreditCard, moduleKey: 'dues' },
    { to: '/agreements', label: 'Convenios', icon: Handshake, moduleKey: 'agreements' },
    { to: '/sales', label: 'Ventas', icon: ShoppingCart, moduleKey: 'sales' },
    { to: '/requests', label: 'Solicitudes', icon: ClipboardList, moduleKey: 'requests' },
    ...(canViewAudit ? [{ to: '/audit', label: 'Auditoria', icon: ShieldAlert }] : []),
    ...(canManageOrganization ? [{ to: '/settings', label: 'Configuracion', icon: Settings }] : []),
  ]

  const navItems = allNavItems.filter((item) => {
    if (!item.moduleKey) return true
    return isModuleEnabled(currentOrganization, item.moduleKey)
  })

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-neutral-950/45 backdrop-blur-sm transition-opacity lg:hidden"
          onClick={onClose}
        />
      )}

      <aside
        className={`
          fixed inset-y-0 left-0 z-40 flex w-[270px] flex-col border-r border-neutral-200 bg-white/90 px-[18px] py-[22px] shadow-none backdrop-blur-[18px] transition-transform duration-300 ease-out dark:border-neutral-800 dark:bg-neutral-900/95
          ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
      >
        <div className="flex items-center justify-between gap-3 px-2 pb-6">
          <div className="flex min-w-0 items-center gap-3">
            {currentOrganization?.logoUrl ? (
              <img
                src={currentOrganization.logoUrl}
                alt={currentOrganization.name}
                className="h-[42px] w-[42px] shrink-0 rounded-[14px] border border-neutral-200 bg-white object-contain p-1 shadow-glow dark:border-neutral-800 dark:bg-neutral-900"
              />
            ) : (
              <div className="grid h-[42px] w-[42px] shrink-0 place-items-center rounded-[14px] bg-gradient-to-br from-primary-500 to-[#8e67ff] text-lg font-extrabold text-white shadow-glow">
                Z
              </div>
            )}
            <div className="min-w-0">
              <span className="block max-w-[170px] truncate text-[18px] font-extrabold leading-tight text-neutral-900 dark:text-neutral-100">
                {currentOrganization?.name || 'Sin organizacion'}
              </span>
              <span className="block truncate text-[11px] font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">Zetyar Nexo</span>
            </div>
          </div>
          <button
            onClick={onClose}
            className="rounded-[13px] p-2 text-neutral-500 hover:bg-neutral-100 hover:text-neutral-900 dark:text-neutral-400 dark:hover:bg-neutral-800 dark:hover:text-white lg:hidden"
            aria-label="Cerrar menu"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="mb-[18px] rounded-[18px] border border-neutral-200 bg-neutral-50 p-3.5 dark:border-neutral-800 dark:bg-neutral-800/45">
          <div className="mb-2 text-[11px] font-bold uppercase text-neutral-500 dark:text-neutral-400">Espacio activo</div>
          <div className="flex items-center gap-2 rounded-[13px] border border-neutral-200 bg-white px-3 py-2 text-sm font-semibold text-neutral-800 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-100">
            <Building2 className="h-4 w-4 text-primary-500" />
            <span className="truncate">{currentOrganization?.name || 'Sin organizacion'}</span>
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto">
          {currentOrganization && (
            <>
              <div className="px-3 pb-2 text-[11px] font-bold uppercase text-neutral-500 dark:text-neutral-400">Menu</div>
              <div className="grid gap-1.5">
                {navItems.map((item) => {
                  const Icon = item.icon
                  return (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      onClick={onClose}
                      className={({ isActive }) => `
                        flex items-center gap-3 rounded-[13px] px-3.5 py-3 text-sm font-semibold transition-colors duration-150
                        ${
                          isActive
                            ? 'bg-primary-50 text-primary-600 dark:bg-primary-500/15 dark:text-primary-200'
                            : 'text-neutral-500 hover:bg-neutral-100 hover:text-neutral-900 dark:text-neutral-400 dark:hover:bg-neutral-800 dark:hover:text-neutral-100'
                        }
                      `}
                    >
                      {({ isActive }) => (
                        <>
                          <Icon className="h-5 w-5 shrink-0" />
                          <span className="flex-1">{item.label}</span>
                          {item.to === '/chat' && hasUnreadChat && !isActive && (
                            <span className="h-2 w-2 shrink-0 rounded-full bg-danger-500" aria-label="Mensajes sin leer" />
                          )}
                        </>
                      )}
                    </NavLink>
                  )
                })}
              </div>
            </>
          )}

          {profile?.isSuperAdmin && (
            <div className={currentOrganization ? "mt-5 border-t border-neutral-200 pt-5 dark:border-neutral-800" : ""}>
              <div className="mb-2 flex items-center justify-between px-3">
                <span className="block text-[10px] font-bold uppercase text-primary-500 dark:text-primary-300">
                  Plataforma SaaS
                </span>
                <Sparkles className="h-3.5 w-3.5 text-primary-500 dark:text-primary-300" />
              </div>
              <NavLink
                to="/admin/saas"
                onClick={onClose}
                className={({ isActive }) => `
                  flex items-center gap-3 rounded-[13px] px-3.5 py-3 text-sm font-semibold transition-colors duration-150
                  ${
                    isActive
                      ? 'bg-primary-50 text-primary-600 dark:bg-primary-500/15 dark:text-primary-200'
                      : 'text-primary-600 hover:bg-primary-50 dark:text-primary-300 dark:hover:bg-primary-500/10'
                  }
                `}
              >
                <ShieldCheck className="h-5 w-5 shrink-0" />
                <span>Panel SaaS</span>
              </NavLink>
            </div>
          )}
        </nav>

        <div className="mt-4 flex items-center justify-between border-t border-neutral-200 pt-4 text-xs text-neutral-500 dark:border-neutral-800 dark:text-neutral-400">
          <span className="flex items-center gap-1.5 font-semibold">
            <Building2 className="h-3.5 w-3.5 text-primary-500" /> Multiempresa
          </span>
          <span className="rounded-full border border-primary-200 bg-primary-50 px-2 py-0.5 text-[10px] font-bold text-primary-700 dark:border-primary-500/30 dark:bg-primary-500/15 dark:text-primary-200">
            v1.0.0
          </span>
        </div>
      </aside>
    </>
  )
}
